import React, { RefObject, useEffect, useRef, useImperativeHandle } from "react";

type DrawFunction = (context: CanvasRenderingContext2D) => void;

const useCanvas = (
  canvasRef: RefObject<HTMLCanvasElement>,
  draw: DrawFunction
): RefObject<HTMLCanvasElement> => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    // Save the current transformation matrix
    context.save();
    
    // Apply sub-pixel offset for crisp lines
    context.translate(0.5, 0.5);
    
    // Call the draw function
    draw(context);
    
    // Restore the transformation matrix
    context.restore();

    return () => {};
  }, [canvasRef, draw]);

  return canvasRef;
};

type CanvasProps = {
  draw: DrawFunction;
  width: number;
  height: number;
} & React.CanvasHTMLAttributes<HTMLCanvasElement>;

const Canvas = React.forwardRef<HTMLCanvasElement, CanvasProps>(
  ({ draw, width, height, ...rest }, ref) => {
    const internalRef = useRef<HTMLCanvasElement>(null);
    useCanvas(internalRef, draw);

    useImperativeHandle(ref, () => internalRef.current!);

    return (
      <canvas
        id="canvas"
        ref={internalRef}
        width={width}
        height={height}
        {...rest}
      />
    );
  }
);

export default Canvas;
